import { PageHero } from '@components/common';
import { getKnowledgeCard } from '@data/knowledgeCards';
import { Shield, CheckCircle, Building2, Globe2, CreditCard, Lock, Car } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

const frameworks = [
  {
    id: 'iso27001',
    name: 'ISO 27001',
    tagline: 'The international standard for information security management.',
    bestFor: 'Organisations of any size seeking a globally recognised security certification.',
    icon: Globe2,
    summary:
      'ISO 27001 provides a systematic approach to managing sensitive company information through people, processes, and technology. Certification demonstrates to customers, regulators, and partners that you take information security seriously.',
    points: [
      'Risk assessment and treatment process',
      'Information Security Management System (ISMS)',
      'Annex A controls covering 93 security measures',
      'Requires external audit and annual surveillance',
    ],
  },
  {
    id: 'soc2',
    name: 'SOC 2',
    tagline: 'The trust standard for service organisations, especially in SaaS.',
    bestFor: 'SaaS, cloud, and technology vendors selling to enterprise US customers.',
    icon: Shield,
    summary:
      'Developed by the AICPA, SOC 2 evaluates how well a service organisation manages data based on five Trust Services Criteria: security, availability, processing integrity, confidentiality, and privacy. Type II is the gold standard for B2B sales.',
    points: [
      'Security (always required) + optional criteria',
      'Requires third-party auditor assessment',
      'Type I is a point-in-time assessment; Type II covers 3–12 months',
      'Often a contractual requirement for US enterprise deals',
    ],
  },
  {
    id: 'nis2',
    name: 'NIS2',
    tagline: 'The EU-wide directive for network and information security.',
    bestFor: 'Essential and important entities operating in the EU and UK equivalents.',
    icon: Building2,
    summary:
      'The NIS2 Directive expands cybersecurity obligations across the EU, covering sectors from energy and transport to healthcare and digital services. It mandates risk management, incident reporting, supply chain security, and board accountability.',
    points: [
      'Applies to essential and important entities',
      'Incident reporting within 24–72 hours',
      'Management body liability for non-compliance',
      'Fines up to €10 million or 2% of global turnover',
    ],
  },
  {
    id: 'cyber-essentials',
    name: 'Cyber Essentials',
    tagline: 'The UK government-backed baseline for cyber hygiene.',
    bestFor: 'UK SMEs, government suppliers, and organisations seeking an affordable baseline.',
    icon: Lock,
    summary:
      'Cyber Essentials is a UK government scheme that helps organisations protect themselves against common cyber threats. It focuses on five technical controls and is increasingly required for public sector contracts.',
    points: [
      'Firewall configuration',
      'Secure configuration',
      'User access control',
      'Malware protection and patch management',
    ],
  },
  {
    id: 'nist-csf',
    name: 'NIST CSF',
    tagline: 'A flexible, risk-based framework for managing cyber risk.',
    bestFor:
      'Organisations looking for a pragmatic, non-certification approach to improving security.',
    icon: Shield,
    summary:
      'The NIST Cybersecurity Framework organises security activities into six functions: Govern, Identify, Protect, Detect, Respond, and Recover. It is voluntary, widely adopted in the US, and increasingly used globally as a strategic planning tool.',
    points: [
      'Six core functions: Govern, Identify, Protect, Detect, Respond, Recover',
      'Governance focus added in CSF 2.0',
      'No certification body; self-assessment or third-party gap analysis',
      'Highly flexible across industries and maturity levels',
    ],
  },
  {
    id: 'dora',
    name: 'DORA',
    tagline: 'Digital Operational Resilience Act for financial services in the EU.',
    bestFor: 'Banks, insurers, investment firms, and their critical ICT third-party providers.',
    icon: Building2,
    summary:
      'DORA sets uniform requirements for the security of network and information systems supporting financial entities in the EU. It introduces strict rules for ICT risk management, incident reporting, resilience testing, and third-party oversight.',
    points: [
      'ICT risk management framework',
      'Digital operational resilience testing',
      'Third-party risk management for critical providers',
      'Harmonised incident reporting to regulators',
    ],
  },
  {
    id: 'pci-dss',
    name: 'PCI DSS',
    tagline: 'The payment card industry data security standard.',
    bestFor: 'Any organisation that stores, processes, or transmits cardholder data.',
    icon: CreditCard,
    summary:
      'PCI DSS is a set of security standards designed to ensure that all companies that accept, process, store, or transmit credit card information maintain a secure environment. Non-compliance can result in fines and loss of card processing privileges.',
    points: [
      'Secure network and systems',
      'Cardholder data protection',
      'Access control measures',
      'Regular monitoring and testing of networks',
    ],
  },
  {
    id: 'gdpr-security',
    name: 'GDPR (Security Lens)',
    tagline: 'The data protection regulation with strict security implications.',
    bestFor: 'Any organisation processing personal data of EU or UK residents.',
    icon: Lock,
    summary:
      'While GDPR is primarily a privacy regulation, it imposes direct security obligations. Organisations must implement appropriate technical and organisational measures to protect personal data and report breaches within 72 hours.',
    points: [
      'Privacy by design and default',
      'Appropriate technical and organisational security measures',
      '72-hour breach notification requirement',
      'Potential fines up to 4% of global turnover',
    ],
  },
  {
    id: 'tisax',
    name: 'TISAX',
    tagline: 'The information security assessment standard for the automotive industry.',
    bestFor: 'Automotive OEMs, Tier 1/2 suppliers, and service providers in the EU and UK.',
    icon: Car,
    summary:
      "TISAX (Trusted Information Security Assessment Exchange) is the European automotive industry's standard for information security assessments. Based on ISO 27001 and ISO 27002, it includes VDA-specific requirements and is often mandatory for supplier relationships.",
    points: [
      'Built on ISO 27001 and ISO 27002 standards',
      'VDA ISA questionnaire with automotive-specific controls',
      'Three assessment levels depending on data sensitivity',
      'Widely required by OEMs and Tier 1 suppliers',
    ],
  },
  {
    id: 'uk-fca-pra',
    name: 'UK FCA / PRA Operational Resilience',
    tagline: 'Operational resilience requirements for UK financial services.',
    bestFor: 'UK banks, insurers, asset managers, building societies, and critical fintechs.',
    icon: Building2,
    summary:
      'The FCA and PRA require UK financial firms to identify important business services, set impact tolerances for disruption, and demonstrate they can remain within those tolerances during severe but plausible scenarios.',
    points: [
      'Identify and map important business services',
      'Set and test impact tolerances',
      'Self-assessment and board attestation',
      'Regular scenario testing and lessons-learned reporting',
    ],
  },
];

export default function CybersecurityFrameworksPage() {
  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': ['Article', 'TechArticle'],
    headline: 'Cybersecurity Frameworks Explained',
    description:
      'A plain-English guide to the standards and regulations that shape modern security programmes.',
    url: 'https://thecisonetwork.com/knowledge-hub/cybersecurity-frameworks',
    image: 'https://thecisonetwork.com/images/social.png',
    datePublished: '2026-04-02T00:00:00+01:00',
    dateModified: '2026-04-02T00:00:00+01:00',
    inLanguage: 'en-GB',
    articleSection: 'Knowledge Hub',
    author: {
      '@type': 'Person',
      name: 'Richard Midwinter',
      url: 'https://www.linkedin.com/in/r-i-c-h-a-r-d',
      image: 'https://thecisonetwork.com/images/team/cto.jpg',
      jobTitle: 'CTO',
      worksFor: {
        '@type': 'Organization',
        name: 'The CISO Network',
        url: 'https://thecisonetwork.com',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'The CISO Network',
      logo: {
        '@type': 'ImageObject',
        url: 'https://thecisonetwork.com/images/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://thecisonetwork.com/knowledge-hub/cybersecurity-frameworks',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <PageHero
        title="Cybersecurity Frameworks Explained"
        subtitle="A plain-English guide to the standards and regulations that shape modern security programmes."
        eyebrow="Knowledge Hub"
        backgroundImage={getKnowledgeCard('cybersecurity-frameworks')?.image}
        backgroundAlt={getKnowledgeCard('cybersecurity-frameworks')?.imageAlt}
        seoTitle="Cybersecurity Frameworks Explained | The CISO Network"
        seoDescription="A plain-English guide to ISO 27001, SOC 2, NIS2, Cyber Essentials, NIST CSF, DORA, PCI DSS and GDPR from a security leadership perspective."
      />

      {/* Intro */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Which Framework Matters for Your Business?
            </h2>
            <p className="text-lg text-muted-foreground">
              Security frameworks can feel like an alphabet soup of acronyms. The reality is
              simpler: each one exists to help you identify risks, protect assets, and demonstrate
              trust to customers and regulators. Below is a quick-reference guide to the frameworks
              our fractional CISOs work with every day.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {frameworks.map(fw => (
              <div
                key={fw.id}
                id={fw.id}
                className="p-8 rounded-xl bg-background border border-border scroll-mt-24"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                    <fw.icon className="h-5 w-5 text-secondary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-foreground">{fw.name}</h3>
                </div>

                <p className="text-sm text-secondary font-medium mb-3">{fw.tagline}</p>
                <p className="text-muted-foreground text-sm mb-4">{fw.summary}</p>

                <div className="mb-4">
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                    Best for
                  </p>
                  <p className="text-sm text-muted-foreground">{fw.bestFor}</p>
                </div>

                <ul className="space-y-2">
                  {fw.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-secondary shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to choose */}
      <section className="py-24 bg-muted">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              How to Choose
            </h2>

            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  You sell B2B software to US enterprises
                </h3>
                <p className="text-muted-foreground text-sm">
                  Start with <strong>SOC 2 Type II</strong>. It is the de facto trust credential for
                  SaaS vendors. Many buyers will not sign a contract without it.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  You operate in the UK and bid for government contracts
                </h3>
                <p className="text-muted-foreground text-sm">
                  <strong>Cyber Essentials Plus</strong> is often mandatory. Add{' '}
                  <strong>ISO 27001</strong> if you want broader commercial credibility.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  You are an EU-based critical infrastructure provider
                </h3>
                <p className="text-muted-foreground text-sm">
                  <strong>NIS2</strong> is now in force and carries personal liability for
                  management. If you are in financial services, add <strong>DORA</strong>.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  You want a strategic roadmap, not a certification
                </h3>
                <p className="text-muted-foreground text-sm">
                  Use <strong>NIST CSF</strong> to baseline your maturity and prioritise gaps. It
                  pairs well with any certification you pursue later.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  You are in the automotive supply chain
                </h3>
                <p className="text-muted-foreground text-sm">
                  <strong>TISAX</strong> is increasingly mandatory for OEM and Tier 1 supplier
                  relationships in Europe. It demonstrates that your information security meets
                  automotive industry expectations.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  You are a UK financial services firm
                </h3>
                <p className="text-muted-foreground text-sm">
                  The <strong>FCA and PRA Operational Resilience</strong> regime requires you to map
                  important services, set impact tolerances, and prove you can recover within them.
                  Board attestation is expected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Not Sure Where to Start?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Our fractional CISOs help organisations cut through the framework maze, choose the right
            certifications, and build programmes that pass audit without slowing the business down.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
